import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation, Trans } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { onPhoneNumberChange } from '@utils/helpers';
import { useBranding, useClient } from '@utils/api';
import { Service } from '@utils/api/aboboApiTypes';
import dateFnsLocales from '@utils/dateFnsLocales';
import { useBookingContext } from '@contexts/BookingContext';
import { createPatientInformationSchema } from '../../validators/patientInformation';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';


type PatientInformationStepProps = {
  selectedService?: Service;
};

const PatientInformationStep = ({ selectedService }: PatientInformationStepProps) => {
  const { t, i18n } = useTranslation();
  const { branding } = useBranding();
  const { client } = useClient();
  const { bookingData, updateBookingData, registerStepValidation, unregisterStepValidation } = useBookingContext();

  // Create dedicated form for patient information
  const form = useForm({
    resolver: yupResolver(createPatientInformationSchema(t)),
    defaultValues: {
      firstName: bookingData.firstName,
      middleName: bookingData.middleName,
      preferredFirstName: bookingData.preferredFirstName,
      lastName: bookingData.lastName,
      phone: bookingData.phone,
      email: bookingData.email,
      platformPreference: bookingData.platformPreference,
      smsOptIn: bookingData.smsOptIn,
    }
  });
  
  const {
    control,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
    handleSubmit,
    trigger
  } = form;
  
  const values = watch();

  // Update booking context when form values change
  useEffect(() => {
    const subscription = watch((value) => {
      updateBookingData({
        firstName: value.firstName || '',
        middleName: value.middleName || '',
        preferredFirstName: value.preferredFirstName || '',
        lastName: value.lastName || '',
        phone: value.phone || '',
        email: value.email || '',
        platformPreference: (value.platformPreference as 'sms' | 'email') || 'sms',
        smsOptIn: value.smsOptIn || false,
      });
    });
    return () => subscription.unsubscribe();
  }, [watch, updateBookingData]);

  // Register validation function with booking context
  useEffect(() => {
    registerStepValidation('patientInformation', async () => {
      const result = await trigger();
      return result;
    });
    return () => unregisterStepValidation('patientInformation');
  }, [trigger, registerStepValidation, unregisterStepValidation]);

  if (!branding) return null;

  return (
    <Form {...form}>
      <form
        className="space-y-5 px-1"
        aria-label={t('booking.form.patientInfoFormLabel')}
        noValidate
        onSubmit={(e) => e.preventDefault()}
      >
        <div>
          <Separator aria-hidden="true" role="separator" className="mb-4"/>

          {selectedService?.appointmentTimeRequired && (
            <div className="pt-2 space-y-2 sticky top-0 z-10" role="group" aria-label={t('booking.form.confirmationTitle')}>
              <p className="text-sm">{t('booking.form.confirmationTitle')}</p>
              <p className="text-base font-medium">
                {t("booking.form.dateTime", {
                  date: format(parseISO(bookingData.appointmentsAttributes[0].scheduledAt), "ccccccc, MMM dd yyyy", { 
                    locale: dateFnsLocales[i18n.language] 
                  }), 
                  time: format(parseISO(bookingData.appointmentsAttributes[0].scheduledAt), "h:mm a", { 
                    locale: dateFnsLocales[i18n.language] 
                  })
                })}
              </p>
            </div>
          )}
        </div>
        
        <div className="space-y-4 text-left">
          <FormField
            control={control}
            name="firstName"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel htmlFor="firstName">
                  {t('booking.form.firstName.name')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="firstName"
                    onChange={(e) => {
                      clearErrors('firstName');
                      field.onChange(e);
                    }}
                    className="w-full"
                    placeholder="e.g. Aerin"
                    aria-describedby={t('aria.form.firstName')}
                    aria-invalid={fieldState.error ? 'true' : 'false'}
                    aria-required="true"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="preferredFirstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="preferredFirstName">
                  {t('booking.form.preferredFirstName.name')} {t('common.optional')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="preferredFirstName"
                    className="w-full"
                    placeholder={t('booking.form.preferredFirstName.placeholder')}
                    aria-describedby={t('aria.form.preferredFirstName')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="middleName"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel htmlFor="middleName">
                  {t('booking.form.middleName.name')} {t('common.optional')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="middleName"
                    className="w-full"
                    placeholder="e.g. Elizabeth"
                    aria-describedby={t('aria.form.middleName')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="lastName"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel htmlFor="lastName">
                  {t('booking.form.lastName.name')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="lastName"
                    onChange={(e) => {
                      clearErrors('lastName');
                      field.onChange(e);
                    }}
                    className="w-full"
                    placeholder="e.g. Smith"
                    aria-describedby={t('aria.form.lastName')}
                    aria-invalid={fieldState.error ? 'true' : 'false'}
                    aria-required="true"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="platformPreference"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel htmlFor="platformPreference">
                  {t('booking.form.platformPreference.title')}
                </FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  items={{
                    sms: t('booking.form.platformPreference.smsOption'),
                    email: t('booking.form.platformPreference.emailOption'),
                  }}
                >
                  <FormControl>
                    <SelectTrigger
                      id="platformPreference"
                      aria-describedby={t('aria.form.platformPreference')}
                      aria-invalid={fieldState.error ? 'true' : 'false'}
                      aria-required="true"
                    >
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="sms">{t('booking.form.platformPreference.smsOption')}</SelectItem>
                    <SelectItem value="email">{t('booking.form.platformPreference.emailOption')}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="phone"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel htmlFor="phone">
                  {t('booking.form.phone.title')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    onChange={(e) => {
                      clearErrors('phone');
                      const formatted = onPhoneNumberChange(e);
                      field.onChange(formatted);
                    }}
                    className="w-full"
                    placeholder="e.g. (555) 123-4567"
                    aria-describedby={t('aria.form.phone')}
                    aria-invalid={fieldState.error ? 'true' : 'false'}
                    aria-required="true"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {values.platformPreference === 'email' && (
          <FormField
            control={control}
            name="email"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel htmlFor="email">
                  {t('booking.form.email.title')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    onChange={(e) => {
                      clearErrors('email');
                      field.onChange(e);
                    }}
                    className="w-full"
                    placeholder="e.g. aerin@example.com"
                    aria-describedby={t('aria.form.email')}
                    aria-invalid={fieldState.error ? 'true' : 'false'}
                    aria-required="true"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />)}

          {values.platformPreference === 'sms' && (
            <FormField
              control={control}
              name="smsOptIn"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      id="smsOptIn"
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        clearErrors('smsOptIn');
                        field.onChange(checked);
                      }}
                      aria-describedby="smsOptIn-description"
                      aria-invalid={fieldState.error ? true : undefined}
                      aria-required="true"
                      className="mt-1"
                    />
                  </FormControl>
                  <div className="space-y-1">
                    <label htmlFor="smsOptIn" id="smsOptIn-description" className="text-sm leading-normal cursor-pointer">
                      <Trans
                        i18nKey="booking.form.smsOptIn"
                        values={{
                          companyName: 'My ClinicAlly',
                          stop: 'STOP',
                          help: 'HELP',
                        }}
                        components={{
                          termsLink: (
                            <a
                              href={`https://${client?.slug}.myclinically.com/privacy`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-teal-700 hover:underline"
                              aria-label={t('booking.form.termsLinkLabel')}
                            />
                          ),
                          privacyLink: (
                            <a
                              href={`https://${client?.slug}.myclinically.com/privacy`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-teal-700 hover:underline"
                              aria-label={t('booking.form.privacyLinkLabel')}
                            />
                          ),
                        }}
                      />
                    </label>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          )}
        </div>
      </form>
    </Form>
  );
};

export default PatientInformationStep;
